import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { GraduationCap, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { homeFor } from "@/components/ProtectedRoute";
import MainSiteLink from "@/components/MainSiteLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SIDE_IMG =
  "https://images.pexels.com/photos/7255432/pexels-photo-7255432.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(homeFor(user.role));
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(homeFor(u.role));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("errors.login_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src={SIDE_IMG} alt={t("login.side_alt")} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-heading text-3xl font-semibold tracking-tight">{t("login.side_title")}</h2>
          <p className="mt-2 text-white/90 leading-relaxed">{t("login.side_text")}</p>
        </div>
      </div>

      <div className="relative flex items-center justify-center p-6 pt-16 bg-background">
        <MainSiteLink className="absolute top-6 right-6" />
        <div className="w-full max-w-sm animate-fade-in">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-lg font-semibold tracking-tight">{t("brand_ats")}</span>
          </div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2">{t("login.title")}</h1>
          <p className="text-muted-foreground text-sm mb-8">{t("login.subtitle")}</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-sm font-medium mb-2 block">{t("common.email")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12" data-testid="login-email" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">{t("common.password")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12" data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full h-12" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("login.submit")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
